import { useEffect, useState, useMemo, useCallback } from "react";
import { Row, Container, Col, Alert, Button, OverlayTrigger, Popover } from "react-bootstrap";
import { MultiSelect } from "react-multi-select-component";
import { useSelector } from "react-redux";
import toastService from "../../_services/toastService";
import SatAPI from "../../_services/sat";
import "../../scss/contingent.scss";
import BudgetSourcesTable from "./components/BudgetSourcesTable";
import ClusterDistributionGrid from "./components/ClusterDistributionGrid";

const api = SatAPI();

const LS_KEY = 'SAT-BudgetSourcesProgrammeFilter';

const loadVisibleIds = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const buildAllocationValues = (allocs) => {
  const result = {};
  allocs.forEach(a => {
    if (a.amountFTE) result[`${a.budgetSourceID}-${a.programmeID}`] = parseFloat(a.amountFTE).toFixed(2);
  });
  return result;
};

const parseAmount = (v) => {
  const n = parseFloat(String(v ?? '').replace(',', '.'));
  return isNaN(n) ? null : n;
};

function Contingent() {
  const snapshot = useSelector((state) => state.snapshot.fileName);

  const [loading, setLoading] = useState(true);
  const [budgetSources, setBudgetSources] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [allocationValues, setAllocationValues] = useState({});
  const [visibleProgrammeIds, setVisibleProgrammeIds] = useState(loadVisibleIds);
  const [budgetSourcesOpen, setBudgetSourcesOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [srcs, allocs, allProgs, allProgsWithFTE, tTasks, tAssignments] = await Promise.all([
        api.getBudgetSources(snapshot),
        api.getBudgetSourceAllocations(snapshot),
        api.getProgrammes(snapshot),
        api.getProgrammesWithFTE(snapshot),
        api.getTasks(snapshot),
        api.getTaskAssignments(snapshot),
      ]);

      const fteMap = new Map(allProgsWithFTE.map(p => [p.programmeID, p]));
      allProgs.forEach(p => {
        const fteProg = fteMap.get(p.ID);
        if (fteProg) {
          //p.fte = calculateRoundedProgrammeVTEPreFetched(collegas, assignments, taskAssignments, p, currentParallels, general, cs, ects);
          p.fte = fteProg.fte / 100;
          //p.availableFTE = calculateAvailableFTEPreFetched(collegas, p);
          p.availableFTE = fteProg.availableFte / 100;
          p.budgetFTE = fteProg.budgetFTE;
        }
      });

      setBudgetSources([...srcs].sort((a, b) => a.name.localeCompare(b.name)));
      setAllocations(allocs);
      setAllocationValues(buildAllocationValues(allocs));
      setClusters(allProgs.filter(p => p.isCluster).sort((a, b) => a.name.localeCompare(b.name)));
      setProgrammes(allProgs);
      // Attach only non-deleted assignments to each task
      tTasks.forEach(task => {
        task.assignments = tAssignments.filter(a => a.taskID === task.ID && !a.deletedAt);
      });
      setTasks(tTasks);
    } catch (error) {
      toastService.send({ title: "An error occurred", message: `${error}` });
    } finally {
      setLoading(false);
    }
  }, [snapshot]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const allocationMap = useMemo(
    () => new Map(allocations.map(a => [`${a.budgetSourceID}-${a.programmeID}`, a])),
    [allocations]
  );

  // Only "Andere" programmes (programmeType === 2)
  const allColumnProgrammes = useMemo(
    () => programmes
      .filter(p => p.programmeType === 2)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [programmes]
  );

  const programmeOptions = useMemo(
    () => allColumnProgrammes.map(p => ({ label: p.name, value: p.ID })),
    [allColumnProgrammes]
  );

  const selectedProgrammeOptions = useMemo(
    () => visibleProgrammeIds === null
      ? programmeOptions
      : programmeOptions.filter(o => visibleProgrammeIds.includes(o.value)),
    [programmeOptions, visibleProgrammeIds]
  );

  const columnProgrammes = useMemo(
    () => visibleProgrammeIds === null
      ? allColumnProgrammes
      : allColumnProgrammes.filter(p => visibleProgrammeIds.includes(p.ID)),
    [allColumnProgrammes, visibleProgrammeIds]
  );

  const handleProgrammeFilterChange = (selected) => {
    const next = selected.length === programmeOptions.length ? null : selected.map(o => o.value);
    setVisibleProgrammeIds(next);
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  };

  const getAllocation = (sourceID, programmeID) => {
    const alloc = allocationMap.get(`${sourceID}-${programmeID}`);
    return alloc ? alloc.amountFTE : 0;
  };

  const getProgrammeColumnTotal = (programmeID) =>
    budgetSources.reduce((sum, src) => sum + getAllocation(src.ID, programmeID), 0);

  const getTotalCredits = () =>
    clusters.reduce((sum, cl) => sum + (cl.credit || 0), 0);

  const handleAllocationChange = (sourceID, programmeID, value) => {
    setAllocationValues(prev => ({ ...prev, [`${sourceID}-${programmeID}`]: value }));
  };

  const handleAllocationBlur = (sourceID, programmeID, value) => {
    const n = parseAmount(value);
    if (n !== null) {
      setAllocationValues(prev => ({ ...prev, [`${sourceID}-${programmeID}`]: n.toFixed(2) }));
    }
  };

  const saveAllChanges = async () => {
    setSaving(true);
    try {
      const getAllocationId = (sourceID, programmeID) => {
        const alloc = allocationMap.get(`${sourceID}-${programmeID}`);
        return alloc ? alloc.ID : null;
      };

      const changed = Object.entries(allocationValues).filter(([key, value]) => {
        const [sourceID, programmeID] = key.split('-').map(Number);
        const orig = allocationMap.get(`${sourceID}-${programmeID}`);
        return (parseAmount(value) ?? 0) !== (Number(orig?.amountFTE) || 0);
      });

      await Promise.all(changed.map(([key, value]) => {
        const [sourceID, programmeID] = key.split('-').map(Number);
        const existingId = getAllocationId(sourceID, programmeID);
        const amountFTE = parseAmount(value) || null;

        if (!existingId && !amountFTE) return Promise.resolve();

        return existingId
          ? api.updateBudgetSourceAllocation(existingId, { budgetSourceID: sourceID, programmeID, amountFTE })
          : api.createBudgetSourceAllocation({ budgetSourceID: sourceID, programmeID, amountFTE });
      }));

      const newAllocations = await api.getBudgetSourceAllocations(snapshot);
      setAllocations(newAllocations);
      setAllocationValues(buildAllocationValues(newAllocations));
    } catch (error) {
      toastService.send({ title: "Fout bij opslaan", message: `${error}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <i className="fad fa-spinner-third fa-spin fa-5x" />
      </div>
    );
  }

  const totalStp = getTotalCredits();
  const werProgramme = programmes.find(p => p.name.trim().toUpperCase() === 'WER');
  const unitProgramme = programmes.find(p => p.name.trim().toLowerCase() === 'unit');
  const werBudgetTotal = werProgramme ? getProgrammeColumnTotal(werProgramme.ID) : 0;
  const unitTaskVTE = unitProgramme?.fte || 0;
  const variabelDeelOpleidingen = werBudgetTotal - unitTaskVTE;
  const missingProgrammes = [
    !werProgramme && 'WER',
    !unitProgramme && 'Unit',
  ].filter(Boolean);

  const programmeFilterPopover = (
    <Popover>
      <Popover.Header as="h3">Filteren</Popover.Header>
      <Popover.Body>
        <h5>Opleidingen</h5>
        <MultiSelect
          options={programmeOptions}
          value={selectedProgrammeOptions}
          onChange={handleProgrammeFilterChange}
          labelledBy="Select"
        />
      </Popover.Body>
    </Popover>
  );

  return (
    <Container fluid className="contingent-page">
      <Row className="align-items-center">
        <Col>
          <h1>Contingentverdeling</h1>
        </Col>
        {budgetSourcesOpen && (
          <Col xs="auto">
            <OverlayTrigger trigger="click" placement="left" overlay={programmeFilterPopover} rootClose>
              <Button variant="secondary">
                <i className="far fa-filter" />
              </Button>
            </OverlayTrigger>
          </Col>
        )}
      </Row>

      {missingProgrammes.length > 0 && (
        <Row className="mt-3">
          <Col>
            <Alert variant="warning">
              {missingProgrammes.join(' en ')} {missingProgrammes.length === 1 ? 'werd' : 'werden'} niet gevonden als opleiding. De berekening van het variabele deel klopt mogelijk niet.
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="mt-4">
        <Col>
          {budgetSources.length === 0 ? (
            <Alert variant="info">
              Er zijn nog geen budgetbronnen aangemaakt.
            </Alert>
          ) : (
            <BudgetSourcesTable
              budgetSources={budgetSources}
              columnProgrammes={columnProgrammes}
              allocationValues={allocationValues}
              allocationMap={allocationMap}
              snapshot={snapshot}
              saving={saving}
              onAllocationChange={handleAllocationChange}
              onAllocationBlur={handleAllocationBlur}
              onSave={saveAllChanges}
              open={budgetSourcesOpen}
              onToggle={() => setBudgetSourcesOpen(o => !o)}
            />
          )}
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <ClusterDistributionGrid
            clusters={clusters}
            programmes={programmes}
            totalStp={totalStp}
            variabelDeelOpleidingen={variabelDeelOpleidingen}
            werBudgetTotal={werBudgetTotal}
            unitTaskVTE={unitTaskVTE}
            budgetSources={budgetSources}
            allocationMap={allocationMap}
          />
        </Col>
      </Row>
    </Container>
  );
}

export default Contingent;
